import * as React from 'react'
import { useState, useRef, useEffect } from 'react'
import {
  ContextChips,
  type PedagogicalContext,
  type ContextOptions,
} from '@sphere/shared/ui/design-system'
import {
  Search,
  Notification,
  User,
  Settings,
  Help,
  Logout,
  Moon,
  Sun,
  Menu,
  Bullhorn,
  WarningAlt,
  ErrorFilled,
  Information,
} from '@carbon/icons-react'
import { useNavigate } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'
import { fr } from 'date-fns/locale'
import { useThemeStore } from '@sphere/shared/data-access/store'
import { User as UserType } from '@sphere/auth/domain'
import {
  SphereLogoLight as SphereLogoLight,
  SphereLogoDark as SphereLogoDark,
} from '@sphere/shared/assets'
import styles from './TopBar.module.scss'

/**
 * Notification dans le TopBar
 */
export interface Notification {
  id: string
  title: string
  description?: string
  timestamp: Date
  read: boolean
  type?: 'info' | 'warning' | 'success' | 'error'
}

export interface TopBarProps {
  context?: PedagogicalContext
  contextOptions?: ContextOptions
  onContextChange?: (
    type: 'establishment' | 'schoolYear' | 'class' | 'subject',
    value: any
  ) => void
  user: UserType
  notifications?: Notification[]
  /** Total non-lu réel côté serveur ; si absent, dérivé du tableau `notifications` passé (souvent tronqué). */
  unreadCount?: number
  onOpenSearch?: () => void
  onLogout?: () => void
  onNotificationClick?: (notification: Notification) => void
  sidebarCollapsed?: boolean
  className?: string
  onToggleSidebar?: () => void
}

function NotificationTypeIcon({ type }: { type?: Notification['type'] }) {
  switch (type) {
    case 'error':
      return <ErrorFilled size={14} className={styles.notifIconUrgent} />
    case 'warning':
      return <WarningAlt size={14} className={styles.notifIconHigh} />
    case 'success':
      return <Information size={14} className={styles.notifIconLow} />
    default:
      return <Bullhorn size={14} className={styles.notifIconNormal} />
  }
}

/**
 * TopBar - Barre supérieure Carbon style
 */
export function TopBar({
  context,
  contextOptions,
  onContextChange,
  user,
  notifications = [],
  unreadCount: unreadCountProp,
  onOpenSearch,
  onLogout,
  onNotificationClick,
  sidebarCollapsed = false,
  onToggleSidebar,
  className,
}: TopBarProps) {
  const navigate = useNavigate()
  const { theme, setTheme } = useThemeStore()
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [notifMenuOpen, setNotifMenuOpen] = useState(false)
  const userMenuRef = useRef<HTMLDivElement>(null)
  const notifMenuRef = useRef<HTMLDivElement>(null)

  const unreadCount =
    unreadCountProp ?? notifications.filter(n => !n.read).length
  const userInitials =
    `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`.toUpperCase()

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target as Node)
      ) {
        setUserMenuOpen(false)
      }
      if (
        notifMenuRef.current &&
        !notifMenuRef.current.contains(event.target as Node)
      ) {
        setNotifMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <header className={`${styles.header} ${className || ''}`}>
      {/* Section Gauche */}
      <div className={styles.leftSection}>
        {/* Toggle Sidebar */}
        <button
          className={styles.toggleButton}
          onClick={onToggleSidebar}
          title={sidebarCollapsed ? 'Ouvrir le menu' : 'Fermer le menu'}
        >
          <Menu size={24} />
        </button>

        {/* Logo */}
        <div className={styles.logoContainer} onClick={() => navigate('/')}>
          <div className={styles.logoWrapper}>
            <img
              src={theme === 'dark' ? SphereLogoDark : SphereLogoLight}
              alt="Sphere"
              className={styles.logoImage}
            />
          </div>
        </div>
      </div>

      {/* Section Droite */}
      <div className={styles.rightSection}>
        {/* Search */}
        {/* <div className={styles.searchContainer} onClick={onOpenSearch}>
          <Search size={16} className={styles.searchIcon} />
          <span className={styles.searchPlaceholder}>Rechercher...</span>
        </div> */}

        {/* Search icon mobile */}
        <button
          className={`${styles.iconButton} ${styles.mobileOnly}`}
          onClick={onOpenSearch}
          title="Rechercher"
        >
          <Search size={20} />
        </button>

        {/* Notifications */}
        <div className={styles.dropdown} ref={notifMenuRef}>
          <button
            className={styles.iconButton}
            onClick={() => setNotifMenuOpen(!notifMenuOpen)}
            title={`Notifications (${unreadCount} non lues)`}
          >
            <Notification size={20} />
            {unreadCount > 0 && (
              <span className={styles.notificationBadge}>
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          {notifMenuOpen && (
            <div className={styles.notifDropdown}>
              <div className={styles.notifDropdownHeader}>
                <span className={styles.notifDropdownTitle}>Notifications</span>
                {unreadCount > 0 && (
                  <span className={styles.notifDropdownCount}>
                    {unreadCount} non lue{unreadCount > 1 ? 's' : ''}
                  </span>
                )}
              </div>

              {notifications.length === 0 ? (
                <div className={styles.notifEmpty}>
                  <Notification size={28} className={styles.notifEmptyIcon} />
                  <p className={styles.notifEmptyText}>Aucune notification</p>
                </div>
              ) : (
                <ul className={styles.notifList}>
                  {notifications.slice(0, 5).map(notif => (
                    <li key={notif.id}>
                      <button
                        type="button"
                        className={`${styles.notifItem} ${!notif.read ? styles.notifItemUnread : ''}`}
                        onClick={() => {
                          onNotificationClick?.(notif)
                          setNotifMenuOpen(false)
                        }}
                      >
                        <div className={styles.notifIconWrap} aria-hidden>
                          <NotificationTypeIcon type={notif.type} />
                        </div>
                        <div className={styles.notifBody}>
                          <div className={styles.notifTopRow}>
                            <span className={styles.notifTitle}>
                              {notif.title}
                            </span>
                            <div className={styles.notifTimeGroup}>
                              {!notif.read && (
                                <span
                                  className={styles.notifUnreadDot}
                                  aria-label="Non lu"
                                />
                              )}
                              <time
                                className={styles.notifTime}
                                dateTime={notif.timestamp.toISOString()}
                              >
                                {formatDistanceToNow(notif.timestamp, {
                                  addSuffix: true,
                                  locale: fr,
                                })}
                              </time>
                            </div>
                          </div>
                          {notif.description && (
                            <p className={styles.notifPreview}>
                              {notif.description}
                            </p>
                          )}
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              {notifications.length > 0 && (
                <button
                  type="button"
                  className={styles.notifViewAll}
                  onClick={() => {
                    navigate('/notifications')
                    setNotifMenuOpen(false)
                  }}
                >
                  Voir toutes les notifications
                </button>
              )}
            </div>
          )}
        </div>

        {/* Settings / Help / Theme Shortcuts */}
        <button
          className={styles.iconButton}
          onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
          title={theme === 'dark' ? 'Mode clair' : 'Mode sombre'}
        >
          {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
        </button>

        {/* User Menu */}
        <div className={styles.dropdown} ref={userMenuRef}>
          <button
            className={styles.userButton}
            onClick={() => setUserMenuOpen(!userMenuOpen)}
            title="Menu utilisateur"
          >
            <div className={styles.userAvatar}>{userInitials}</div>
          </button>

          {userMenuOpen && (
            <div className={styles.dropdownContent}>
              <div className={styles.dropdownLabel}>
                <div
                  style={{ fontWeight: 600, color: 'var(--cds-text-primary)' }}
                >
                  {user.firstName} {user.lastName}
                </div>
                <div style={{ fontSize: '0.75rem', marginTop: '2px' }}>
                  {user.email}
                </div>
              </div>
              <div className={styles.dropdownDivider} />

              <button
                className={styles.dropdownItem}
                onClick={() => {
                  navigate('/profile')
                  setUserMenuOpen(false)
                }}
              >
                <User size={16} />
                Mon Profil
              </button>

              <button
                className={styles.dropdownItem}
                onClick={() => {
                  navigate('/settings')
                  setUserMenuOpen(false)
                }}
              >
                <Settings size={16} />
                Paramètres
              </button>

              <div className={styles.dropdownDivider} />

              <button
                className={styles.dropdownItem}
                onClick={() => {
                  navigate('/docs')
                  setUserMenuOpen(false)
                }}
              >
                <Help size={16} />
                Aide
              </button>

              <button
                className={`${styles.dropdownItem} ${styles.dropdownItemDanger}`}
                onClick={() => {
                  onLogout?.()
                  setUserMenuOpen(false)
                }}
              >
                <Logout size={16} />
                Se déconnecter
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  )
}
